/**
 * connectivity.ts — pure connectivity + wizard-step derivation (the tested
 * core of the Connectivity wizard). No React, no fetch — the hooks
 * (`useConnectivity`, the wizard's own probes) assemble a
 * `ConnectivitySnapshot` and these functions decide what it means.
 *
 * Steps (the wizard walks them in this order):
 *
 *   services — the distillery backend (Docker stack) is reachable at /health
 *   storage  — the VSKI platform exists and has an admin (installer step)
 *   login    — the backend holds a working platform credential (minted key)
 *   api      — this client has a WS connection configured (AuthContext)
 *   done     — summary; every step reachable again via its Change link
 *
 * Storage is REQUIRED: the VSKI platform is the only project storage (no
 * local-SQLite fallback), so green = backend healthy + WS connection
 * configured, and the wizard cannot be dismissed past the storage step.
 */
import type { VskiStatus } from '../vski/vskiConfig';

export type WizardStep = 'services' | 'storage' | 'login' | 'api' | 'done';

/** Everything the derivation needs, gathered from the probes + AuthContext. */
export interface ConnectivitySnapshot {
  /** The configured WS connection (AuthContext.wsUrl); null = never connected. */
  wsUrl: string | null;
  /** /health probe of the backend origin; null = not checked yet. */
  backendOk: boolean | null;
  /** /api/vski/status payload; null = not fetched / fetch failed. */
  vski: VskiStatus | null;
}

export type StorageState = 'ok' | 'missing' | 'unknown';

export interface Connectivity {
  /** Green state: backend healthy + a WS connection configured. */
  connected: boolean;
  backendReady: boolean;
  storageState: StorageState;
}

export function deriveConnectivity(snap: ConnectivitySnapshot): Connectivity {
  const backendReady = snap.backendOk === true;
  const storageState: StorageState = !snap.vski
    ? 'unknown'
    : snap.vski.configured && snap.vski.platform.authOk !== false
      ? 'ok'
      : 'missing';
  return { connected: backendReady && !!snap.wsUrl, backendReady, storageState };
}

// ─── Per-step completion (the stepper chips + Next gating) ─────────────────

/** The backend is up (health probe ok). */
export function servicesStepDone(snap: ConnectivitySnapshot): boolean {
  return snap.backendOk === true;
}

/** The installer part is past: the platform is reachable and has an admin
 *  (or storage is already configured). */
export function storageStepDone(snap: ConnectivitySnapshot): boolean {
  const v = snap.vski;
  if (!v) return false;
  if (v.configured) return true;
  return v.platform.reachable && v.platform.hasAdmins;
}

/** The backend holds a working platform credential. */
export function loginStepDone(snap: ConnectivitySnapshot): boolean {
  const v = snap.vski;
  return !!v && v.configured && v.platform.authOk !== false;
}

/** A WS connection is configured on this client. */
export function apiStepDone(snap: ConnectivitySnapshot): boolean {
  return !!snap.wsUrl;
}

/** Where the wizard opens: the first step whose exit condition is NOT met.
 *  Called only after the probes settle (health + vski status), so a fully
 *  configured stack lands on 'done' instead of flashing through services. */
export function initialWizardStep(snap: ConnectivitySnapshot): WizardStep {
  if (!servicesStepDone(snap)) return 'services';
  if (!storageStepDone(snap)) {
    const v = snap.vski;
    return v && v.platform.reachable && v.platform.hasAdmins ? 'login' : 'storage';
  }
  if (!loginStepDone(snap)) return 'login';
  return apiStepDone(snap) ? 'done' : 'api';
}
